import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enGB } from 'date-fns/locale';
import {
  ArrowBack,
  EditOutlined,
  DeleteOutline,
  ShieldOutlined,
  PaymentsOutlined,
  EventRepeatOutlined,
  ReceiptLongOutlined,
  AddCardOutlined,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import {
  PolicyTypeIcon,
  policyType,
  policyStatus,
  frequency,
  annualPremium,
  dueInfo,
  hasEnded,
  daysUntil,
  nextDueAfter,
  parseDate,
  toDateString,
  formatDate,
  formatCover,
  formatRupees,
} from '../../utils/insurance';
import { SummaryTile } from '../utilities/FastagManager';
import PolicyDialog from './PolicyDialog';
import GuidedTour from '../../components/tour/GuidedTour';
import TourButton from '../../components/tour/TourButton';
import useTour from '../../components/tour/useTour';
import { TOURS } from '../../components/tour/tours';

// "3 years 2 months left" / "ended 4 months ago"
const timeLeft = (endDate) => {
  const days = daysUntil(endDate);
  if (days === null) return '';
  const abs = Math.abs(days);
  const years = Math.floor(abs / 365);
  const months = Math.floor((abs % 365) / 30.44);
  const parts = [];
  if (years) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (months) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  const text = parts.join(' ') || `${abs} day${abs === 1 ? '' : 's'}`;
  return days < 0 ? `ended ${text} ago` : `${text} left`;
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{value || '—'}</Typography>
  </Box>
);

const InsuranceDetails = () => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();

  const [policy, setPolicy] = useState(null);
  const [premiums, setPremiums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const detailsTour = useTour('insurance-details', { ready: !loading && !notFound });

  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false);
  const [editingPremium, setEditingPremium] = useState(null);
  const [form, setForm] = useState({ date: new Date(), amount: '', notes: '', advanceDue: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletePremium, setDeletePremium] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [policyResult, premiumsResult] = await Promise.all([
      supabase.from('insurance_policies').select('*').eq('id', policyId).eq('user_id', user.id).maybeSingle(),
      supabase
        .from('insurance_premiums')
        .select('*')
        .eq('policy_id', policyId)
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false }),
    ]);
    if (policyResult.error) console.error('Error fetching insurance policy:', policyResult.error);
    if (premiumsResult.error) {
      console.error('Error fetching premiums:', premiumsResult.error);
      notify('Failed to fetch premium history', 'error');
    }
    setPolicy(policyResult.data || null);
    setNotFound(!policyResult.data);
    setPremiums(premiumsResult.data || []);
    setLoading(false);
  }, [supabase, user, policyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPaid = useMemo(() => premiums.reduce((sum, p) => sum + (Number(p.amount) || 0), 0), [premiums]);

  // Next due date after recording a premium (only when adding, and the policy has one)
  const currentDue = parseDate(policy?.next_due_date);
  const advancedDue = currentDue ? nextDueAfter(currentDue, policy?.premium_frequency) : null;
  const canAdvance = Boolean(!editingPremium && advancedDue && policy?.status === 'active');

  // ---- Premiums ----
  const openAddPremium = () => {
    setEditingPremium(null);
    setForm({ date: new Date(), amount: policy?.premium_amount ? String(policy.premium_amount) : '', notes: '', advanceDue: true });
    setFormError('');
    setPremiumDialogOpen(true);
  };

  const openEditPremium = (premium) => {
    setEditingPremium(premium);
    setForm({ date: parseDate(premium.date), amount: String(premium.amount), notes: premium.notes || '', advanceDue: false });
    setFormError('');
    setPremiumDialogOpen(true);
  };

  const closePremiumDialog = () => {
    if (saving) return;
    setPremiumDialogOpen(false);
    setEditingPremium(null);
  };

  const handleSavePremium = async () => {
    const amount = parseFloat(form.amount);
    if (!form.date || isNaN(form.date.getTime())) {
      setFormError('Please pick the date you paid');
      return;
    }
    if (!isFinite(amount) || amount <= 0) {
      setFormError('Please enter an amount greater than zero');
      return;
    }
    setSaving(true);
    const data = { date: toDateString(form.date), amount, notes: form.notes.trim() };
    const { error } = editingPremium
      ? await supabase.from('insurance_premiums').update(data).eq('id', editingPremium.id).eq('user_id', user.id)
      : await supabase.from('insurance_premiums').insert({ ...data, policy_id: policyId, user_id: user.id });

    if (error) {
      setSaving(false);
      console.error('Error saving premium:', error);
      setFormError(`Failed to ${editingPremium ? 'update' : 'save'} the premium`);
      return;
    }

    let message = editingPremium ? 'Premium updated successfully' : 'Premium recorded successfully';
    if (canAdvance && form.advanceDue) {
      const { error: dueError } = await supabase
        .from('insurance_policies')
        .update({ next_due_date: toDateString(advancedDue) })
        .eq('id', policyId)
        .eq('user_id', user.id);
      if (dueError) {
        console.error('Error moving next due date:', dueError);
        message = 'Premium recorded, but the next due date could not be updated';
      } else {
        message = `Premium recorded. Next due ${formatDate(advancedDue)}`;
      }
    }
    setSaving(false);
    setPremiumDialogOpen(false);
    setEditingPremium(null);
    notify(message);
    fetchData();
  };

  const handleDeletePremium = async () => {
    const premium = deletePremium;
    setDeletePremium(null);
    const { error } = await supabase.from('insurance_premiums').delete().eq('id', premium.id).eq('user_id', user.id);
    if (error) {
      console.error('Error deleting premium:', error);
      notify('Failed to delete the premium', 'error');
      return;
    }
    notify('Premium deleted. Check the next due date if it needs moving back.');
    fetchData();
  };

  // ---- Render ----
  if (loading && !policy) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (notFound) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Policy not found</Typography>
          <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/insurance')}>Back to Insurance</Button>
        </Card>
      </Box>
    );
  }

  const type = policyType(policy.policy_type);
  const status = policyStatus(policy.status);
  const freq = frequency(policy.premium_frequency);
  const due = dueInfo(policy);
  const ended = hasEnded(policy);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconButton onClick={() => navigate('/insurance')} aria-label="Back to Insurance">
            <ArrowBack />
          </IconButton>
          <Box sx={{
            width: 48, height: 48, borderRadius: '50%', flexShrink: 0, display: { xs: 'none', sm: 'grid' }, placeItems: 'center',
            bgcolor: alpha(type.color, 0.1), color: type.color,
          }}>
            <PolicyTypeIcon type={policy.policy_type} sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }} noWrap>{policy.policy_name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {[type.label, policy.insurer, policy.policy_number && `No. ${policy.policy_number}`].filter(Boolean).join(' · ')}
              </Typography>
              <Chip size="small" label={status.label} color={status.color} variant={policy.status === 'active' ? 'filled' : 'outlined'} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} data-tour="insurance-actions">
          <TourButton onClick={detailsTour.start} />
          <Button variant="outlined" startIcon={<EditOutlined />} onClick={() => setPolicyDialogOpen(true)}>Edit</Button>
          <Button variant="contained" startIcon={<AddCardOutlined />} onClick={openAddPremium}>Record Premium</Button>
        </Box>
      </Box>

      {due && due.days <= 30 && (
        <Alert severity={due.severity === 'error' ? 'error' : 'warning'} sx={{ mb: 2 }}>
          Premium of {formatRupees(policy.premium_amount)} {due.days < 0 ? 'was due' : 'is due'} on {formatDate(policy.next_due_date)} ({due.label.toLowerCase()}).
          After paying, click <b>Record Premium</b> and the next due date moves forward.
        </Alert>
      )}
      {ended && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={() => setPolicyDialogOpen(true)}>Update</Button>}
        >
          This policy's end date ({formatDate(policy.end_date)}) has passed. If it has matured, expired or been renewed, update it.
        </Alert>
      )}

      <Paper data-tour="insurance-tiles" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, sm: 1.5 } }}>
        <SummaryTile icon={<ShieldOutlined />} color="success" value={formatCover(policy.sum_assured)} label={policy.policy_type === 'motor' ? 'Cover (IDV)' : 'Sum assured / cover'} />
        <SummaryTile
          icon={<PaymentsOutlined />}
          color="primary"
          value={formatRupees(policy.premium_amount)}
          label={freq.perYear > 1 ? `${freq.label} · ${formatRupees(annualPremium(policy))}/year` : freq.label}
        />
        <SummaryTile icon={<ReceiptLongOutlined />} color="info" value={formatRupees(totalPaid)} label={`Premiums paid (${premiums.length})`} />
        <SummaryTile
          icon={<EventRepeatOutlined />}
          color={due && due.days <= 30 ? 'warning' : 'info'}
          value={policy.premium_frequency === 'single' ? 'Paid' : formatDate(policy.next_due_date)}
          label={policy.premium_frequency === 'single' ? 'Single premium' : 'Next due'}
        />
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3, alignItems: 'start' }}>
        {/* Policy details */}
        <Paper data-tour="insurance-info" sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Policy Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <DetailRow label="Insured / Covered" value={policy.insured} />
            <DetailRow label="Nominee" value={policy.nominee} />
            <DetailRow label="Policy Number" value={policy.policy_number} />
            <DetailRow label="Insurer" value={policy.insurer} />
            <DetailRow label="Start Date" value={policy.start_date && formatDate(policy.start_date)} />
            <DetailRow
              label={['term', 'life'].includes(policy.policy_type) ? 'Maturity / End' : 'Expiry / End'}
              value={policy.end_date && `${formatDate(policy.end_date)}\n${timeLeft(policy.end_date)}`}
            />
            <DetailRow label="Agent / Advisor" value={policy.agent} />
            <DetailRow label="Status" value={status.label} />
          </Box>
          {policy.notes && (
            <Box sx={{ mt: 2 }}>
              <DetailRow label="Notes" value={policy.notes} />
            </Box>
          )}
        </Paper>

        {/* Premium history */}
        <Paper data-tour="insurance-history" sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Premiums Paid</Typography>
          {premiums.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No premiums recorded yet.</Typography>
              <Button sx={{ mt: 1.5 }} startIcon={<AddCardOutlined />} onClick={openAddPremium}>Record Premium</Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {premiums.map((premium) => (
                <Box
                  key={premium.id}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 1, borderRadius: 2,
                    border: '1px solid', borderColor: (t) => alpha(t.palette.divider, 0.6),
                  }}
                >
                  <Box sx={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1), color: 'primary.main', '& svg': { fontSize: 19 },
                  }}>
                    <PaymentsOutlined />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{formatDate(premium.date)}</Typography>
                    {premium.notes && (
                      <Typography variant="caption" color="text.secondary" component="div" noWrap>{premium.notes}</Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {formatRupees(premium.amount, 2)}
                  </Typography>
                  <IconButton size="small" aria-label="Edit premium" onClick={() => openEditPremium(premium)}>
                    <EditOutlined sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" aria-label="Delete premium" onClick={() => setDeletePremium(premium)} sx={{ ml: -0.75, '&:hover': { color: 'error.main' } }}>
                    <DeleteOutline sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'right' }}>
                Total paid: {formatRupees(totalPaid, 2)}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      <PolicyDialog
        open={policyDialogOpen}
        policy={policy}
        onClose={() => setPolicyDialogOpen(false)}
        onSaved={(message) => { setPolicyDialogOpen(false); notify(message); fetchData(); }}
        supabase={supabase}
        user={user}
      />

      {/* Record / edit premium */}
      <Dialog open={premiumDialogOpen} onClose={closePremiumDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingPremium ? 'Edit Premium' : 'Record Premium'}</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25, pt: 1 }}>
              <DatePicker
                label="Paid On *"
                value={form.date}
                onChange={(value) => { setForm({ ...form, date: value }); setFormError(''); }}
                maxDate={new Date()}
                format="dd MMM yyyy"
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Amount"
                required
                autoFocus
                type="number"
                inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                value={form.amount}
                onChange={(e) => { setForm({ ...form, amount: e.target.value }); setFormError(''); }}
              />
              <TextField
                label="Notes (Optional)"
                placeholder="e.g., Paid via HDFC credit card, receipt in email"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
              {canAdvance && (
                <FormControlLabel
                  control={<Checkbox checked={form.advanceDue} onChange={(e) => setForm({ ...form, advanceDue: e.target.checked })} />}
                  label={
                    <Typography variant="body2">
                      Move next due date from {formatDate(currentDue)} to <b>{formatDate(advancedDue)}</b>
                    </Typography>
                  }
                />
              )}
              {formError && <Alert severity="error">{formError}</Alert>}
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePremiumDialog} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePremium} disabled={saving}>
            {saving ? 'Saving…' : editingPremium ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete premium */}
      <Dialog open={Boolean(deletePremium)} onClose={() => setDeletePremium(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Premium</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the premium of {formatRupees(deletePremium?.amount, 2)} paid on {deletePremium && formatDate(deletePremium.date)}?
            The next due date is not changed; edit the policy if it needs moving back.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePremium(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeletePremium}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <GuidedTour steps={TOURS['insurance-details']} open={detailsTour.open} onClose={detailsTour.close} />
    </Box>
  );
};

export default InsuranceDetails;
